import { useState, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import useMarks from '../components/useMarks';
import useDaysNumber from '../useDaysNumber';

import useP4RContext from '@/store/P4RContext';

import { timelineInterval, timelineLastHoursInterval } from '@/data/app';

export default function useTimelineLogic(
  startDate?: Dayjs,
  endDate?: Dayjs,
  comparison?: boolean,
  toCompare?: boolean,
  controlDisabled?: boolean,
) {
  const {
    licenceAreaSelected,
    changeLicenceAreaSelected,
    eventLicenceAreaSelected,
    changeEventLicenceAreaSelected,
    currentTime: currentTimeSingle,
    changeCurrentTime,
    startingTime,
    changeStartingTime,
    currentTimeComparison,
    changeCurrentTimeComparison,
    timelinePlaying,
    changeTimelinePlaying,
  } = useP4RContext();

  const selectedLicenceArea = comparison
    ? eventLicenceAreaSelected
    : licenceAreaSelected;

  let currentTime = currentTimeSingle;

  if (comparison) {
    currentTime = currentTimeComparison;
  }

  const currentHour = startDate?.startOf('hour').hour();

  const daysNumber = useDaysNumber(startDate, endDate);

  const marks = useMarks(startDate, daysNumber);

  const [pickerValue, setPickerValue] = useState<Dayjs>(dayjs());
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleTimelineValue = (hour: Dayjs, starting?: boolean) => {
    const step = marks.findIndex((mark) => mark.value.isSame(hour), 'hour');

    const nextStep = step === Object.keys(marks).length - 1 ? 0 : step + 1;
    const nextHour = marks[nextStep].value;

    if (comparison) {
      changeCurrentTimeComparison({
        currentTime: [hour, step, marks.length],
        nextTime: [nextHour, nextStep],
      });
    } else {
      changeCurrentTime({
        currentTime: [hour, step, marks.length],
        nextTime: [nextHour, nextStep],
      });
    }

    if (starting) {
      changeStartingTime({
        currentTime: [hour, step, marks.length],
        nextTime: [nextHour, nextStep],
      });
    }
  };

  useEffect(() => {
    if (!startDate) return;

    // Check if currentTime exists and is valid for the current date range
    const preservedTime = currentTime[0];

    // Check if preserved time exists in the marks array (marks IS the source of truth)
    let shouldPreserve = false;
    let step = -1;

    if (preservedTime !== null) {
      step = marks.findIndex((mark) =>
        mark.value.isSame(preservedTime, 'hour'),
      );
      shouldPreserve = step >= 0 && step < marks.length;
    }

    // Check if we're already at the preserved time (to prevent unnecessary updates)
    const isAlreadyAtPreservedTime =
      shouldPreserve &&
      preservedTime !== null &&
      currentTime[1] === step &&
      currentTime[0]?.isSame(preservedTime, 'hour');

    if (shouldPreserve && preservedTime !== null && !isAlreadyAtPreservedTime) {
      // Preserve the timeline value - reuse handleTimelineValue
      setPickerValue(preservedTime);
      handleTimelineValue(preservedTime, true);
    } else if (!shouldPreserve || isAlreadyAtPreservedTime) {
      // Only reset if we're not already at the preserved time
      if (!isAlreadyAtPreservedTime) {
        setPickerValue(startDate);
        if (!comparison) {
          handleTimelineValue(startDate, true);
        } else {
          handleTimelineValue(startDate.set('hour', 0), true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate?.format('L')]);

  const handleCurrentTime = (
    step: number,
    manual: boolean,
    reset?: boolean,
  ) => {
    if (manual) changeTimelinePlaying(false);

    const hour = marks[step].value;

    const nextStep = step === Object.keys(marks).length - 1 ? 0 : step + 1;
    const nextHour = marks[nextStep].value;

    if (reset) {
      changeCurrentTimeComparison({
        currentTime: [(startDate as Dayjs).set('hour', 0), 0, marks.length],
        nextTime: [(startDate as Dayjs).set('hour', 0).add(1, 'day'), 1],
      });

      changeCurrentTime(startingTime);
    } else {
      if (!comparison) {
        changeCurrentTime({
          currentTime: [hour, step, marks.length],
          nextTime: [nextHour, nextStep],
        });
      }

      if (comparison) {
        changeCurrentTimeComparison({
          currentTime: [hour, step, marks.length],
          nextTime: [nextHour, nextStep],
        });
      }
    }
  };

  useEffect(() => {
    const newTime = currentTime[1] + 1;

    // Auto-play if: (controlDisabled AND not explicitly paused) OR explicitly playing
    const shouldPlay =
      timelinePlaying === true ||
      (timelinePlaying === null && controlDisabled === true);

    // Always clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    if (shouldPlay && newTime >= marks.length) {
      const waitingForCompared =
        currentTimeSingle[1] < (currentTimeSingle[2] as number) - 1 ||
        currentTimeComparison[1] < (currentTimeComparison[2] as number) - 1;
      if (!waitingForCompared || !toCompare) {
        setTimeout(() => {
          handleCurrentTime(currentHour as number, false, true);
        }, timelineLastHoursInterval * 1000);
      }
    } else if (shouldPlay) {
      intervalRef.current = setInterval(() => {
        handleCurrentTime(newTime, false);
      }, timelineInterval * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlDisabled, timelinePlaying, currentTime]);

  const handleSelectPicker = (value: Dayjs) => {
    let newValue = value;

    const valueDay = value.day();

    if (pickerValue.day() !== valueDay) {
      const currentDay = valueDay === startDate?.day();

      if (currentDay) {
        newValue = value.set('hour', comparison ? 0 : (currentHour as number));
      }
    }

    setPickerValue(newValue);
    handleTimelineValue(newValue);
  };

  const handleTimeLinePlay = () => {
    // Toggle between true and false, never null (null is only for initial state)
    // If null (initial), start playing (true)
    // If false (paused), resume playing (true)
    // If true (playing), pause (false)
    changeTimelinePlaying(timelinePlaying !== true);
  };

  return {
    selectedLicenceArea,
    currentTime,
    daysNumber,
    marks,
    pickerValue,
    handleCurrentTime,
    handleSelectPicker,
    handleTimeLinePlay,
    changeLicenceAreaSelected,
    changeEventLicenceAreaSelected,
    changeTimelinePlaying,
  };
}
